const PT = require("prop-types");
const React = require("react");
const WonkyButton = require("./WonkyButton");
const WhiteCardModal = require("./WhiteCardModal");
const AssetsUtil = require("../util/AssetsUtil");
const Sounds = require("../util/Sounds");
const {
	NAV_SURFACE,
	trackViewSummarySurface,
	trackSummaryLessonNavClick,
} = require("../../trainer/util/TrainerLessonNavMixpanel");

const styles = require("./styles/ContinuousPlayStreakModal.css");

function headingForStarsEarned(totalStarsEarnedInStreak) {
	const n =
		typeof totalStarsEarnedInStreak === "number" &&
		Number.isFinite(totalStarsEarnedInStreak)
			? Math.max(0, Math.floor(totalStarsEarnedInStreak))
			: 0;
	if (n <= 0) {
		return "Stars earned - your brain is working up a sweat!";
	}
	const unit = n === 1 ? "star" : "stars";
	return `${n} ${unit} earned - your brain is working up a sweat!`;
}

function ContinuousPlayStreakModal(props) {
	const {
		user,
		styleType,
		onClose,
		nextLessonButtonProps,
		totalStarsEarnedInStreak,
		setURL,
	} = props;

	const musicTrackToRestoreRef = React.useRef(null);

	const mixpanelExtras = React.useMemo(
		() =>
			typeof totalStarsEarnedInStreak === "number"
				? {stars_obtained: totalStarsEarnedInStreak}
				: undefined,
		[totalStarsEarnedInStreak]
	);

	/* eslint-disable react-hooks/exhaustive-deps -- Depend on stable ids (not `user` / full `messageData`) so duplicate View Summary is not sent when the parent passes new object references for the same account and payload. */
	React.useEffect(() => {
		if (!user) {
			return;
		}
		trackViewSummarySurface(
			NAV_SURFACE.HALLWAY_CELEBRATION,
			user,
			mixpanelExtras
		);
	}, [user?.model?.userID, mixpanelExtras]);
	/* eslint-enable react-hooks/exhaustive-deps */

	React.useEffect(() => {
		musicTrackToRestoreRef.current = Sounds.getCurrentMusicTrackName();
		Sounds.stopMusic(false);
		Sounds.playSound("cpv-streak");
	}, []);

	const resumeMusicThen = React.useCallback(callback => {
		const track = musicTrackToRestoreRef.current;
		if (track) {
			Sounds.playMusic(track);
		}
		callback();
	}, []);

	const handleClickNextLessonButton = React.useCallback(() => {
		trackSummaryLessonNavClick(
			NAV_SURFACE.HALLWAY_CELEBRATION,
			"next_lesson",
			user,
			mixpanelExtras
		);
		const {linkTo, handleClick} = nextLessonButtonProps;
		if (linkTo) {
			resumeMusicThen(() => {
				setURL(linkTo);
				onClose();
			});
		} else if (handleClick) {
			resumeMusicThen(() => {
				handleClick();
				onClose();
			});
		}
	}, [
		mixpanelExtras,
		nextLessonButtonProps,
		resumeMusicThen,
		setURL,
		user,
		onClose,
	]);

	const handleClickBackToAllLessonsButton = React.useCallback(() => {
		trackSummaryLessonNavClick(
			NAV_SURFACE.HALLWAY_CELEBRATION,
			"back_to_path",
			user,
			mixpanelExtras
		);
		resumeMusicThen(onClose);
	}, [user, mixpanelExtras, resumeMusicThen, onClose]);

	return (
		<WhiteCardModal
			onClose={() => resumeMusicThen(onClose)}
			styleType={styleType}
			contentClassName={styles.headingContent}
			buttonsExtraClassName={
				nextLessonButtonProps ? undefined : styles.buttonsSingleCta
			}
			title={headingForStarsEarned(totalStarsEarnedInStreak)}
			buttons={
				nextLessonButtonProps && (
					<>
						<WonkyButton
							{...nextLessonButtonProps}
							className={WhiteCardModal.BUTTON_CLASS}
							handleClick={handleClickNextLessonButton}
						/>
						<WonkyButton
							className={WhiteCardModal.BUTTON_CLASS}
							handleClick={handleClickBackToAllLessonsButton}
							buttonType="trapFlippedWide"
							buttonText="BACK TO ALL LESSONS"
						/>
					</>
				)
			}
			levelType="aboveAll"
		>
			<div className={styles.illustrationWrap}>
				<img
					src={AssetsUtil.getContinuousPlayStreakIllustration()}
					className={styles.illustration}
					alt=""
				/>
			</div>
		</WhiteCardModal>
	);
}

ContinuousPlayStreakModal.displayName = "ContinuousPlayStreakModal";

ContinuousPlayStreakModal.propTypes = {
	user: PT.object.isRequired,
	styleType: PT.string,
	totalStarsEarnedInStreak: PT.number,
	onClose: PT.func.isRequired,
	setURL: PT.func.isRequired,
	nextLessonButtonProps: PT.obj,
};

module.exports = ContinuousPlayStreakModal;
